/**
 * GTM DataLayer 通用上报工具
 *
 * 通过 window.dataLayer.push 将事件推送给 GTM 容器，
 * 由 GTM 转发至 Google Ads 等下游平台。
 */

import UserStore, { IDENTITYKEY } from '@/store/User';
import { getCookie, setCookie } from '@/utils';
import { AIFunction } from '@/utils/const';
import { APP_VERSION } from '@/version';

// ============ 类型定义 ============

/** nav_menu 触发类型参数 */
interface LoginPopupNavMenu {
  trigger_type: 'nav_menu';
  nav_menu_item: 'login_btn' | 'user_account_icon' | 'upgrade_btn' | 'invite_earn_btn';
}

/** auth_wall 触发类型参数 */
interface LoginPopupAuthWall {
  trigger_type: 'auth_wall';
  app_name: string;
  feature: string;
  usage_limit?: string;
}

/** cta_click 触发类型参数 */
interface LoginPopupCtaClick {
  trigger_type: 'cta_click';
  cta_content: string;
}

/** login_popup 事件参数联合类型 */
export type LoginPopupParams =
  | LoginPopupNavMenu
  | LoginPopupAuthWall
  | LoginPopupCtaClick;

/** login 事件参数 */
export interface LoginParams {
  method: 'google' | 'apple' | 'email';
  user_id: string;
  subscription_plan: string;
  subscription_status: 'free' | 'active_subscriber' | 'lapsed_subscriber';
}

/** login_fail 事件参数 */
export interface LoginFailParams {
  method: 'google' | 'apple' | 'email';
  error_note: string;
}

/** email_sign_up_code_validated 事件参数 */
export interface EmailSignUpCodeValidatedParams {
  method: 'email';
}

/** sign_up 事件参数 */
export interface SignUpParams {
  method: 'google' | 'apple' | 'email';
  sign_up_date: string;
  user_id: string;
  subscription_plan: string;
  subscription_status: string;
}

/** avatar_generate_audio 事件参数 */
export interface AvatarGenerateAudioParams {
  loading_time: number;
  app_name: string;
  app_version: string;
  feature: string;
  selected_voice: string;
  voice_tags?: string;
  speed: string;
  duration: string;
}

/** app_use_start 事件参数 */
export interface AppUseStartParams {
  app_name: string;
  app_version: string;
  feature: string;
  credits_required: number;
  [key: string]: unknown;
}

/** creation_preview 事件参数 */
export interface CreationPreviewParams {
  app_name: string;
  feature: string;
  model?: string;
}

/** creation_download 事件参数 */
export interface CreationDownloadParams {
  app_name: string;
  feature: string;
  model?: string;
  download_tier: string;
}

/** app_use_resource_upload 事件参数 */
export interface ResourceUploadParams {
  app_name: string;
  app_version: string;
  feature: string;
  resource_format: string;
  resource_size: string;
  resource_option?: string;
}

/** app_use_resource_select_from_library 事件参数 */
export interface ResourceSelectFromLibraryParams {
  app_name: string;
  app_version: string;
  feature: string;
  resource_format: string;
  resource_size?: string;
  resource_option?: string;
}

/** asset_upload 事件参数 */
export interface AssetUploadParams {
  asset_category: 'Image' | 'Video' | 'My Voice';
  resource_format: string;
  resource_size: string;
}

/** asset_delete 事件参数 */
export interface AssetDeleteParams {
  asset_category: 'Image' | 'Video' | 'My Voice';
  resource_format: string;
  resource_size?: string;
}

/** view_pricing_plans — usage_limit 触发类型参数 */
interface ViewPricingPlansUsageLimit {
  trigger_type: 'usage_limit';
  app_name: string;
  app_version: string;
  feature: string;
  usage_limit:
    | 'top_up'
    | 'vip_subtitle_style'
    | 'vip_voice'
    | 'no_watermark_download'
    | 'voice_slot'
    | string;
}

/** view_pricing_plans — nav_menu 触发类型参数 */
interface ViewPricingPlansNavMenu {
  trigger_type: 'nav_menu';
  nav_menu_item: 'pricing_link' | 'upgrade_btn' | 'user_account_panel';
}

/** view_pricing_plans 事件参数联合类型 */
export type ViewPricingPlansParams = ViewPricingPlansUsageLimit | ViewPricingPlansNavMenu;

/** begin_checkout 单个商品参数 */
export interface BeginCheckoutItem {
  item_id: string;
  item_name: string;
  item_variant?: 'Annually' | 'Monthly';
  currency: string;
  price: number;
  quantity: 1;
}

/** begin_checkout 事件参数 */
export interface BeginCheckoutParams {
  ecommerce: {
    items: BeginCheckoutItem[];
  };
}

/** purchase 单个商品参数 */
export interface PurchaseItem {
  item_id: string;
  item_name: string;
  item_variant?: 'Annually' | 'Monthly';
  discount?: number;
  currency: string;
  price: number;
  quantity: 1;
}

/** purchase 事件参数 */
export interface PurchaseParams {
  subscription_plan: string;
  prev_subscription_plan?: string;
  ecommerce: {
    transaction_id: string;
    value: number;
    currency: string;
    coupon: string;
    customer_type: 'new' | 'returning';
    items: PurchaseItem[];
  };
}

/** select_template 事件参数 */
export interface SelectTemplateParams {
  app_name: string;
  app_version: string;
  template_name: string;
  template_category: string;
}

/** app_view 事件参数 */
export interface AppViewParams {
  app_name: string;
  subscription_plan?: string;
  subscription_status?: 'free' | 'active_subscriber' | 'lapsed_subscriber';
}

/** GTM 事件映射表 */
interface GTMEventMap {
  login_popup: LoginPopupParams;
  login: LoginParams;
  login_fail: LoginFailParams;
  email_sign_up_code_validated: EmailSignUpCodeValidatedParams;
  sign_up: SignUpParams;
  avatar_generate_audio: AvatarGenerateAudioParams;
  app_view: AppViewParams;
  app_use_start: AppUseStartParams;
  select_template: SelectTemplateParams;
  creation_preview: CreationPreviewParams;
  creation_download: CreationDownloadParams;
  app_use_resource_upload: ResourceUploadParams;
  app_use_resource_select_from_library: ResourceSelectFromLibraryParams;
  asset_upload: AssetUploadParams;
  asset_delete: AssetDeleteParams;
  view_pricing_plans: ViewPricingPlansParams;
  begin_checkout: BeginCheckoutParams;
  purchase: PurchaseParams;
}

/** 所有已注册的 GTM 事件名 */
export type GTMEventName = keyof GTMEventMap;

// ============ 工具函数 ============

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * 向 GTM dataLayer 推送事件
 *
 * @param eventName - GTM 事件名称
 * @param params - 事件参数（类型安全，按 eventName 自动推断）
 */
export const pushGTMEvent = <T extends GTMEventName>(
  eventName: T,
  params: GTMEventMap[T],
): void => {
  if (typeof window === 'undefined') return;

  console.log(eventName, '===', params);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...params,
  });
};

// ============ AIFunction → 菜单英文名映射 ============

const AI_FUNCTION_TO_MENU_NAME: Partial<Record<AIFunction, string>> = {
  [AIFunction.Text2Video]: 'AI Video Generator',
  [AIFunction.Image2Video]: 'AI Video Generator',
  [AIFunction.Text2Image]: 'AI Image Generator',
  [AIFunction.Image2Image]: 'AI Image Generator',
  [AIFunction.VideoEdit]: 'AI Video Editor',
  [AIFunction.VideoTranslate]: 'Video Translation',
  [AIFunction.ImageTranslate]: 'Image Translation',
  [AIFunction.Avatar2]: 'Avatar Video 2.0',
  [AIFunction.ProductAvatar]: 'Product Avatar',
  [AIFunction.Removal]: 'AI Removal',
  [AIFunction.FaceSwap]: 'Face Swap 2.0',
  [AIFunction.HeadSwap]: 'Face Swap 2.0',
  [AIFunction.VoiceClone]: 'Voice Clone',
  [AIFunction.Text2Speech]: 'Voice Clone',
  [AIFunction.ImageEnhance]: 'Image Enhance',
  [AIFunction.Library]: 'Library',
  [AIFunction.ViralRemake]: 'Ad Clone',
  [AIFunction.ViralTemplate]: 'Template',
};

/**
 * 将 AIFunction enum 转换为菜单英文名称（用于 GTM app_name）
 */
export const getGTMAppName = (fn: AIFunction): string =>
  AI_FUNCTION_TO_MENU_NAME[fn] ?? fn;

// ============ AIFunction → 子功能英文名映射 ============

const AI_FUNCTION_TO_FEATURE_NAME: Partial<Record<AIFunction, string>> = {
  [AIFunction.Text2Video]: 'Text to Video',
  [AIFunction.Image2Video]: 'Image to Video',
  [AIFunction.Text2Image]: 'Text to Image',
  [AIFunction.Image2Image]: 'Image to Image',
  [AIFunction.VideoEdit]: 'AI Video Editor',
  [AIFunction.VideoTranslate]: 'Video Translation',
  [AIFunction.ImageTranslate]: 'Image Translation',
  [AIFunction.Avatar2]: 'Avatar Video 2.0',
  [AIFunction.ProductAvatar]: 'Product Avatar',
  [AIFunction.Removal]: 'AI Removal',
  [AIFunction.FaceSwap]: 'Face Swap 2.0',
  [AIFunction.HeadSwap]: 'Face Swap 2.0',
  [AIFunction.VoiceClone]: 'Voice Clone',
  [AIFunction.Text2Speech]: 'Text to Speech',
  [AIFunction.ImageEnhance]: 'Image Enhance',
  [AIFunction.ViralRemake]: 'Ad Clone',
};

/**
 * 将 AIFunction enum 转换为子功能英文名称（用于 GTM feature）
 */
export const getGTMFeatureName = (fn: AIFunction): string =>
  AI_FUNCTION_TO_FEATURE_NAME[fn] ?? getGTMAppName(fn);

// ============ app_use_start 辅助函数 ============

/**
 * 判断字符串是否为 URL
 */
const isURL = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  return /^(https?:\/\/|data:image\/|blob:)/.test(value);
};

/**
 * 判断字段名是否为敏感字段
 * - 子串匹配：prompt
 * - 精确匹配：text、script（避免误伤 textLang、voicePreviewText 等字段）
 * 注意：title 不在此处处理，由各调用方按需局部处理（避免误伤其他模块）
 */
const isSensitiveField = (key: string): boolean => {
  const substringKeywords = ['prompt'];
  const exactKeys = ['text', 'script'];
  const lower = key.toLowerCase();
  return substringKeywords.some((kw) => lower.includes(kw)) || exactKeys.includes(lower);
};

/**
 * 过滤敏感字段，将 URL 和 prompt 类字段替换为布尔值
 * - URL 类型的值（http/https/data:image/blob:）→ true/false
 * - prompt/text/script 等字段 → true/false
 * - 其他字段保持原值
 */
export const sanitizeSensitiveParams = (
  params: Record<string, unknown>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(params)) {
    // 特定字段名匹配
    if (isSensitiveField(key)) {
      result[key] = Boolean(value && (typeof value === 'string' ? value.trim() : true));
      continue;
    }

    // URL 类型判断（包括 null/undefined 的情况）
    if (value === null || value === undefined || value === '') {
      // 如果字段名看起来像 URL 字段（image/video/audio/url 等），转为 false
      if (/image|video|audio|url|file|path/i.test(key)) {
        result[key] = false;
        continue;
      }
      // 否则保持原值
      result[key] = value;
      continue;
    }

    if (isURL(value)) {
      result[key] = true;
      continue;
    }

    // 数组类型递归处理
    if (Array.isArray(value)) {
      result[key] = value.every((item) => isURL(item))
        ? value.length > 0
        : value.map((item) =>
            typeof item === 'object' && item !== null
              ? sanitizeSensitiveParams(item as Record<string, unknown>)
              : isURL(item)
                ? true
                : item,
          );
      continue;
    }

    // 对象类型递归处理
    if (typeof value === 'object' && value !== null) {
      result[key] = sanitizeSensitiveParams(value as Record<string, unknown>);
      continue;
    }

    // 其他类型保持原值
    result[key] = value;
  }

  return result;
};

/**
 * 更新 localStorage 中的工具使用记录
 * - last_tool_used：每次更新
 * - first_tool_used：仅首次写入
 */
export const updateToolUsageInLocalStorage = (appName: string): void => {
  if (typeof window === 'undefined') return;

  localStorage.setItem('last_tool_used', appName);

  if (!localStorage.getItem('first_tool_used')) {
    localStorage.setItem('first_tool_used', appName);
  }
};

/**
 * 推送 app_use_start 事件并更新 localStorage 工具使用记录
 */
export const trackAppUseStart = (
  feature: AIFunction,
  params: {
    credits_required: number;
    [key: string]: unknown;
  },
): void => {
  const appName = getGTMAppName(feature);

  // 过滤敏感字段（保留 credits_required）
  const { credits_required, ...restParams } = params;
  const sanitizedParams = sanitizeSensitiveParams(restParams);

  pushGTMEvent('app_use_start', {
    app_name: appName,
    app_version: APP_VERSION,
    feature: getGTMFeatureName(feature),
    credits_required,
    ...sanitizedParams,
  });

  updateToolUsageInLocalStorage(appName);
};

// ============ login 事件辅助函数 ============

const DAY_MS = 24 * 60 * 60 * 1000;

const PLAN_LABEL: Record<IDENTITYKEY, string> = {
  freeVip: 'Free',
  basicVip: 'Standard',
  advancedVip: 'Pro',
  enterpriseVip: 'Enterprise',
};

/**
 * 从 currentVip 推导 subscription_plan
 * 复用 logAdapter.ts getDurationType 的月/年判断逻辑
 */
export const getSubscriptionPlan = (): string => {
  const { key, startTime, endTime } = UserStore.getState().currentVip;
  const label = PLAN_LABEL[key] ?? 'Free';

  if (key === 'freeVip' || key === 'enterpriseVip') return label;

  const diff = endTime - startTime;
  const period = diff > 360 * DAY_MS ? 'Annually' : 'Monthly';
  return `${label} - ${period}`;
};

/**
 * 从 currentVip 推导 subscription_status
 * free: 未付费 | active_subscriber: 付费中 | lapsed_subscriber: 曾付费已过期
 */
export const getSubscriptionStatus = (): LoginParams['subscription_status'] => {
  const { key, isExpireUser, recharged } = UserStore.getState().currentVip;

  if (key !== 'freeVip' && !isExpireUser) return 'active_subscriber';
  if (recharged && isExpireUser) return 'lapsed_subscriber';
  return 'free';
};

// ============ sign_up 事件辅助函数 ============

/**
 * 将当前时间格式化为 YYYY.MM.DD（点号分隔、补零、不含时分秒）
 */
export const formatSignUpDate = (): string => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

// ============ resource_upload 辅助函数 ============

/**
 * 从文件名或 URL 提取小写扩展名（不含点号）
 * 例：'photo.JPG' → 'jpg'，'https://cdn.com/video.mp4?token=x' → 'mp4'
 */
export const getResourceFormat = (nameOrUrl: string): string => {
  const withoutQuery = nameOrUrl.split('?')[0];
  const match = withoutQuery.match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : '';
};

/**
 * 将字节数转为整数 MB（向上取整）
 */
export const getFileSizeInMB = (sizeInBytes: number): string =>
  (sizeInBytes / (1024 * 1024)).toFixed(2);

/**
 * 推送 app_use_resource_upload 事件
 */
export const trackResourceUpload = (
  feature: AIFunction,
  file: { name: string; size: number },
  resourceOption?: string,
): void => {
  pushGTMEvent('app_use_resource_upload', {
    app_name: getGTMAppName(feature),
    app_version: APP_VERSION,
    feature: getGTMFeatureName(feature),
    resource_format: getResourceFormat(file.name),
    resource_size: getFileSizeInMB(file.size),
    ...(resourceOption ? { resource_option: resourceOption } : {}),
  });
};

/**
 * 推送 app_use_resource_select_from_library 事件
 */
export const trackResourceSelectFromLibrary = (
  feature: AIFunction,
  file: { name: string; size?: number },
  resourceOption?: string,
): void => {
  pushGTMEvent('app_use_resource_select_from_library', {
    app_name: getGTMAppName(feature),
    app_version: APP_VERSION,
    feature: getGTMFeatureName(feature),
    resource_format: getResourceFormat(file.name),
    ...(file.size != null ? { resource_size: getFileSizeInMB(file.size) } : {}),
    ...(resourceOption ? { resource_option: resourceOption } : {}),
  });
};
